import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardMedia, CardContent,
  Button, Chip, Skeleton, Container, IconButton, useMediaQuery, useTheme
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import CakeIcon from '@mui/icons-material/Cake';
import VerifiedIcon from '@mui/icons-material/Verified';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import { useStore } from '../context/StoreContext';
import useBehaviorTracking from '../hooks/useBehaviorTracking';
import { normalizeImageUrl, onImageErrorSetFallback } from '../utils/imageFallback';

const fixImageUrl = (url) => {
  return normalizeImageUrl(url, 'Product');
};

// Gemini 2.5 Pro - Food color palette: warm, appetizing
const C = {
  bg: '#fffaf5',
  warmRed: '#d4503a',
  darkRed: '#b73a2a',
  orange: '#f58634',
  darkOrange: '#e06f1f',
  cream: '#fef3e2',
  brown: '#5c3d2e',
  lightBrown: '#d4a76a',
  green: '#6b9e3a',
  greenLight: '#e8f5e9',
  white: '#ffffff',
  textLight: '#8a7060',
};

const TemplateFood = () => {
  const [featured, setFeatured] = useState([]);
  const [newProducts, setNewProducts] = useState([]);
  const [hotProducts, setHotProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentBanner, setCurrentBanner] = useState(0);
  const { addToCart } = useCart();
  const { storeConfig, shippingConfig, categories: dbCategories } = useStore();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation('common');
  useBehaviorTracking();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const [featuredRes, newRes, hotRes] = await Promise.all([
          api.get('/products/featured?limit=8'),
          api.get('/products?sort=newest&limit=8'),
          api.get('/products?sort=sales&limit=8')
        ]);
        setFeatured(featuredRes.data || []);
        setNewProducts(newRes.data.items || []);
        setHotProducts(hotRes.data.items || []);
      } catch (error) {
        console.error('Failed to load products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const banners = [
    {
      title: storeConfig.store_name || t('template.food_shop'),
      subtitle: storeConfig.slogan || t('template.food_slogan'),
      bgImage: '/images/banners/banner-food.jpg',
    },
    {
      title: t('template.selected_ingredients'),
      subtitle: t('template.selected_ingredients_sub'),
    },
    {
      title: t('template.free_shipping_promo'),
      subtitle: t('template.free_shipping_fee', { threshold: shippingConfig?.free_shipping_threshold || 799 }),
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => setCurrentBanner(p => (p + 1) % banners.length), 5000);
    return () => clearInterval(timer);
  }, [banners.length]);

  // Gemini: borderRadius 4 (16px), pill-shaped buttons
  const ProductCard = ({ product }) => {
    const discount = product.original_price > product.sale_price
      ? Math.round((1 - product.sale_price / product.original_price) * 100) : 0;

    return (
      <Card sx={{
        height: '100%', display: 'flex', flexDirection: 'column',
        bgcolor: C.white, borderRadius: 4, overflow: 'hidden',
        transition: 'all 0.35s ease',
        boxShadow: '0 2px 12px rgba(212,80,58,0.05)',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: '0 16px 40px rgba(212,80,58,0.12)',
          '& .product-img': { transform: 'scale(1.06)' },
        }
      }}>
        <Box sx={{ position: 'relative', pt: '100%', bgcolor: '#faf5f0', cursor: 'pointer', overflow: 'hidden' }}
          onClick={() => navigate(`/products/${product.id}`)}>
          <CardMedia className="product-img" component="img" image={fixImageUrl(product.image_url)} alt={product.name} loading="lazy"
            onError={(e) => onImageErrorSetFallback(e, product.name)}
            sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain', p: 1.5, transition: 'transform 0.4s ease' }} />
          {discount > 0 && (
            <Chip label={`${discount}% OFF`} size="small"
              sx={{ position: 'absolute', top: 12, left: 12, bgcolor: C.warmRed, color: C.white, fontWeight: 700, fontSize: '0.65rem', height: 24, borderRadius: 3 }} />
          )}
          {product.quantity <= 10 && product.quantity > 0 && (
            <Chip label={t('template.limited_qty', { count: product.quantity })} size="small"
              sx={{ position: 'absolute', top: 12, right: 12, bgcolor: 'rgba(245,134,52,0.9)', color: C.white, fontWeight: 600, fontSize: '0.65rem', height: 24, borderRadius: 3 }} />
          )}
        </Box>
        <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
          <Typography variant="body2" sx={{
            overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', minHeight: 44,
            color: C.brown, fontWeight: 500, cursor: 'pointer', lineHeight: 1.6,
            '&:hover': { color: C.warmRed }
          }} onClick={() => navigate(`/products/${product.id}`)}>{product.name}</Typography>
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ color: C.warmRed, fontWeight: 700, fontSize: '1.2rem' }}>
              ${product.sale_price ? Math.round(Number(product.sale_price)).toLocaleString() : 0}
            </Typography>
            {product.original_price > product.sale_price && (
              <Typography variant="caption" sx={{ color: '#bbb', textDecoration: 'line-through', ml: 1 }}>
                ${product.original_price ? Math.round(Number(product.original_price)).toLocaleString() : 0}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5, flexWrap: 'wrap' }}>
            <Chip icon={<LocalShippingIcon sx={{ fontSize: 11 }} />} label={t('template.fast_delivery')} size="small"
              sx={{ height: 22, fontSize: '0.65rem', bgcolor: C.greenLight, color: C.green, borderRadius: 3, '& .MuiChip-icon': { color: C.green } }} />
          </Box>
        </CardContent>
        <Box sx={{ px: 2.5, pb: 2.5 }}>
          {/* Gemini: pill-shaped CTA button */}
          <Button fullWidth variant="contained" startIcon={<ShoppingCartIcon sx={{ fontSize: 16 }} />}
            onClick={() => addToCart(product.id)} disabled={product.quantity <= 0}
            sx={{
              bgcolor: C.warmRed, borderRadius: 50, textTransform: 'none',
              fontWeight: 700, py: 1.1, fontSize: '0.85rem',
              boxShadow: 'none',
              '&:hover': { bgcolor: C.darkRed, boxShadow: '0 4px 12px rgba(212,80,58,0.3)' },
              '&.Mui-disabled': { bgcolor: '#e8ddd4', color: C.textLight },
            }}>
            {product.quantity > 0 ? t('template.buy_immediately') : t('template.sold_out')}
          </Button>
        </Box>
      </Card>
    );
  };

  const SectionTitle = ({ icon, title, subtitle }) => (
    <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 } }}>
      {icon && <Box sx={{ color: C.orange, mb: 1, '& svg': { fontSize: 32 } }}>{icon}</Box>}
      <Typography variant="h4" sx={{ fontWeight: 800, color: C.brown, fontSize: { xs: '1.5rem', md: '2rem' } }}>{title}</Typography>
      {subtitle && <Typography variant="body2" sx={{ color: C.textLight, mt: 0.5, letterSpacing: '0.05em' }}>{subtitle}</Typography>}
      <Box sx={{ width: 40, height: 3, bgcolor: C.orange, mx: 'auto', mt: 2, borderRadius: 2 }} />
    </Box>
  );

  return (
    <Box sx={{ bgcolor: 'background.default' }}>
      {/* Category chips */}
      <Box sx={{ bgcolor: C.white, py: 1.5, borderBottom: '1px solid rgba(240,224,208,0.6)', overflowX: 'auto', '&::-webkit-scrollbar': { height: 0 } }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', minWidth: 'max-content' }}>
            <Chip icon={<RestaurantIcon sx={{ fontSize: 16 }} />} label={t('template.all_food')} onClick={() => navigate('/products')}
              sx={{ bgcolor: C.warmRed, color: C.white, fontWeight: 600, borderRadius: 50, '& .MuiChip-icon': { color: C.white } }} />
            {dbCategories.slice(0, 6).map(cat => (
              <Chip key={cat.name} label={cat.name} variant="outlined"
                onClick={() => navigate(`/products?category=${encodeURIComponent(cat.name)}`)}
                sx={{ borderColor: C.lightBrown, color: C.brown, borderRadius: 50, '&:hover': { bgcolor: '#fef3e2' } }} />
            ))}
          </Box>
        </Container>
      </Box>

      {/* Gemini: 85vh hero with warm food vibes */}
      <Box sx={{ position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', transition: 'transform 0.6s ease', transform: `translateX(-${currentBanner * 100}%)` }}>
          {banners.map((banner, idx) => (
            <Box key={idx} sx={{
              minWidth: '100%',
              minHeight: { xs: 220, md: 360 },
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              position: 'relative', px: 3,
              bgcolor: theme.palette.primary.main,
              backgroundImage: banner.bgImage ? `url(${banner.bgImage})` : `radial-gradient(circle at 60% 40%, ${theme.palette.secondary.main}66 0%, transparent 50%), radial-gradient(circle at 20% 80%, rgba(107,158,58,0.2) 0%, transparent 40%)`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}>
              <CakeIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 32, mb: 2,
                animation: currentBanner === idx ? 'fadeIn 0.6s ease forwards' : 'none', opacity: currentBanner === idx ? 1 : 0 }} />
              <Typography sx={{
                color: C.white, fontWeight: 800,
                fontSize: { xs: '1.8rem', sm: '2.2rem', md: '3rem' },
                textAlign: 'center', lineHeight: 1.15,
                animation: currentBanner === idx ? 'slideUp 0.7s ease 0.15s forwards' : 'none',
                opacity: currentBanner === idx ? 1 : 0,
              }}>{banner.title}</Typography>
              <Typography variant="body2" sx={{
                color: 'rgba(255,255,255,0.85)', mt: 1.5, letterSpacing: '0.08em', textAlign: 'center',
                fontSize: { xs: '0.8rem', md: '0.9rem' },
                animation: currentBanner === idx ? 'slideUp 0.7s ease 0.3s forwards' : 'none',
                opacity: currentBanner === idx ? 1 : 0,
              }}>{banner.subtitle}</Typography>
              <Button variant="contained" onClick={() => navigate('/products')}
                sx={{
                  mt: 3, bgcolor: 'rgba(255,255,255,0.95)', color: C.warmRed,
                  fontWeight: 700, borderRadius: 50, px: 4, py: 1,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  animation: currentBanner === idx ? 'slideUp 0.7s ease 0.45s forwards' : 'none',
                  opacity: currentBanner === idx ? 1 : 0,
                  '&:hover': { bgcolor: C.white, boxShadow: '0 6px 24px rgba(0,0,0,0.2)' }
                }}>{t('btn.browse_now')}</Button>
            </Box>
          ))}
        </Box>
        <IconButton onClick={() => setCurrentBanner(p => (p - 1 + banners.length) % banners.length)}
          sx={{ position: 'absolute', left: { xs: 8, md: 24 }, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: C.white } }}>
          <ArrowBackIosNewIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <IconButton onClick={() => setCurrentBanner(p => (p + 1) % banners.length)}
          sx={{ position: 'absolute', right: { xs: 8, md: 24 }, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: C.white } }}>
          <ArrowForwardIosIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <Box sx={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 1.5 }}>
          {banners.map((_, i) => (
            <Box key={i} onClick={() => setCurrentBanner(i)}
              sx={{ width: currentBanner === i ? 20 : 8, height: 8, bgcolor: currentBanner === i ? C.white : 'rgba(255,255,255,0.4)', cursor: 'pointer', transition: 'all 0.4s ease', borderRadius: 4 }} />
          ))}
        </Box>
      </Box>

      {/* Gemini: trust/certification strip */}
      <Box sx={{ bgcolor: C.warmRed, py: 1.8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={2} justifyContent="center">
            {[
              { icon: <LocalShippingIcon sx={{ fontSize: 17 }} />, text: t('template.free_shipping_threshold', { threshold: shippingConfig?.free_shipping_threshold || 799 }) },
              { icon: <VerifiedIcon sx={{ fontSize: 17 }} />, text: t('template.quality_cert') },
              { icon: <CardGiftcardIcon sx={{ fontSize: 17 }} />, text: t('template.gift_box') },
              { icon: <RestaurantIcon sx={{ fontSize: 17 }} />, text: t('template.selected_ingredients_label') },
            ].map((item, i) => (
              <Grid item xs={6} sm={3} key={i}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, color: 'rgba(255,255,255,0.9)' }}>
                  {item.icon}
                  <Typography variant="caption" fontWeight={600} sx={{ letterSpacing: '0.05em' }}>{item.text}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        {/* Featured */}
        <SectionTitle icon={<LocalFireDepartmentIcon />} title={t('template.hot_recommend')} subtitle={t('template.hot_recommend_sub')} />
        <Grid container spacing={{ xs: 2, sm: 3, md: 3 }}>
          {loading ? Array(8).fill(0).map((_, i) => (
            <Grid item xs={6} sm={4} md={3} key={i}>
              <Skeleton variant="rectangular" sx={{ pt: '100%', borderRadius: 4 }} />
              <Skeleton width="60%" sx={{ mt: 1.5 }} />
              <Skeleton width="40%" />
            </Grid>
          )) : featured.map(product => (
            <Grid item xs={6} sm={4} md={3} key={product.id}><ProductCard product={product} /></Grid>
          ))}
        </Grid>

        {/* Gemini: Gift box section */}
        <Box sx={{ mt: { xs: 6, md: 10 }, mb: { xs: 6, md: 10 } }}>
          <Grid container spacing={0} sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 8px 30px rgba(212,80,58,0.08)' }}>
            <Grid item xs={12} md={6}>
              <Box sx={{
                bgcolor: C.brown, p: { xs: 4, md: 6 }, height: '100%',
                display: 'flex', flexDirection: 'column', justifyContent: 'center',
                minHeight: { xs: 200, md: 280 },
              }}>
                <CardGiftcardIcon sx={{ color: C.orange, fontSize: 36, mb: 2 }} />
                <Typography variant="h5" sx={{ color: C.white, fontWeight: 700, mb: 2, fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
                  {t('template.gift_first_choice')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.8, mb: 3 }}>
                  {t('template.gift_desc')}
                </Typography>
                <Button variant="outlined" onClick={() => navigate('/products')}
                  sx={{ alignSelf: 'flex-start', borderColor: C.orange, color: C.orange, borderRadius: 50, px: 4, '&:hover': { bgcolor: C.orange, color: C.white } }}>
                  {t('btn.shop_gift_box')}
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{
                bgcolor: C.cream, minHeight: { xs: 200, md: 280 },
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
                backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(245,134,52,0.08) 0%, transparent 70%)',
              }}>
                <CardGiftcardIcon sx={{ color: C.lightBrown, fontSize: 48, mb: 2 }} />
                <Typography sx={{ color: C.lightBrown, letterSpacing: '0.15em', fontSize: '0.85rem' }}>PREMIUM GIFT BOX</Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Hot */}
        {hotProducts.length > 0 && (
          <>
            <SectionTitle icon={<LocalOfferIcon />} title={t('template.super_deals')} subtitle={t('template.super_deals_sub')} />
            <Grid container spacing={{ xs: 2, sm: 3, md: 3 }}>
              {hotProducts.slice(0, 4).map(product => (
                <Grid item xs={6} sm={4} md={3} key={product.id}><ProductCard product={product} /></Grid>
              ))}
            </Grid>
          </>
        )}

        {/* New */}
        {newProducts.length > 0 && (
          <Box sx={{ mt: { xs: 6, md: 10 } }}>
            <SectionTitle icon={<CakeIcon />} title={t('template.new_release')} subtitle={t('template.new_release_sub')} />
            <Grid container spacing={{ xs: 2, sm: 3, md: 3 }}>
              {newProducts.slice(0, 4).map(product => (
                <Grid item xs={6} sm={4} md={3} key={`new-${product.id}`}><ProductCard product={product} /></Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Gemini: trust/certification block */}
        <Box sx={{ mt: { xs: 6, md: 10 }, p: { xs: 4, md: 6 }, bgcolor: C.cream, borderRadius: 4 }}>
          <Grid container spacing={4} justifyContent="center">
            {[
              { icon: <VerifiedIcon />, title: t('template.quality_cert'), desc: t('template.quality_cert_desc'), color: C.green },
              { icon: <LocalShippingIcon />, title: t('template.cold_chain'), desc: t('template.cold_chain_desc'), color: C.warmRed },
              { icon: <CardGiftcardIcon />, title: t('template.gift_packaging'), desc: t('template.gift_packaging_desc'), color: C.orange },
              { icon: <RestaurantIcon />, title: t('template.customer_service'), desc: storeConfig.service_hours || t('template.service_hours_short'), color: C.brown },
            ].map((item, i) => (
              <Grid item xs={6} sm={3} key={i}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ color: item.color, mb: 1.5, '& svg': { fontSize: 36 } }}>{item.icon}</Box>
                  <Typography variant="subtitle2" sx={{ color: C.brown, fontWeight: 700 }}>{item.title}</Typography>
                  <Typography variant="caption" sx={{ color: C.textLight }}>{item.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>

      {/* CSS Animations */}
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(25px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </Box>
  );
};

export default TemplateFood;


